import React, { useState } from 'react';
import { Dialog, Input } from "@material-tailwind/react";
import { X, UserPlus, AlertCircle, Loader2 } from 'lucide-react';

const AddTeamMemberModal = ({ isOpen, onClose, onAdd, isAdding }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        cnic: '',
    });
    const [errorMsg, setErrorMsg] = useState("");

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setErrorMsg(""); // clear error on typing
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name || !formData.email) return;
        setErrorMsg("");
        try {
            await onAdd(formData);
            setFormData({ name: '', email: '', cnic: '' });
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || "Failed to add team member. Please try again.");
        }
    };

    // Clear state on close
    const handleClose = () => {
        setErrorMsg("");
        onClose();
    };

    return (
        <Dialog
            open={isOpen}
            handler={handleClose}
            animate={{
                mount: { scale: 1, y: 0, opacity: 1 },
                unmount: { scale: 0.9, y: -100, opacity: 0 },
            }}
            size="sm"
            className="rounded-2xl bg-white shadow-2xl"
        >
            <div className="flex items-center justify-between border-b border-[#ECE3D5] px-6 py-4">
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cream-input text-brand">
                        <UserPlus size={22} />
                    </div>
                    <h4 className="font-display text-lg font-semibold text-ink">Add New Team Member</h4>
                </div>
                <button
                    type="button"
                    onClick={handleClose}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-[#8C8175] transition-colors hover:bg-cream-input hover:text-ink"
                >
                    <X size={20} />
                </button>
            </div>

            <form onSubmit={handleSubmit}>
                <div className="space-y-4 p-6">
                    <p className="text-sm text-[#8C8175]">
                        Enter the details for the new team member. An email with instructions to set their password will be sent immediately upon creation. If they do not receive it in their inbox or spam folder, they can use the &ldquo;Forgot Password&rdquo; option on the login screen.
                    </p>

                    {errorMsg && (
                        <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                            <AlertCircle size={18} className="mt-0.5 shrink-0" />
                            <span>{errorMsg}</span>
                        </div>
                    )}

                    <div className="space-y-5 pt-1">
                        <Input label="Full Name" name="name" value={formData.name} onChange={handleChange} size="lg" required />
                        <Input label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} size="lg" required />
                        <Input label="CNIC (Optional)" name="cnic" value={formData.cnic} onChange={handleChange} size="lg" />
                    </div>
                </div>

                <div className="flex justify-end gap-3 border-t border-[#ECE3D5] p-6">
                    <button
                        type="button"
                        onClick={handleClose}
                        className="rounded-xl px-6 py-2.5 font-display text-sm font-semibold text-[#6B675F] transition-colors hover:bg-cream"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isAdding || !formData.name || !formData.email}
                        className="flex items-center gap-2 rounded-xl bg-brand px-6 py-2.5 font-display text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isAdding ? <Loader2 size={18} className="animate-spin" /> : <UserPlus size={18} />}
                        {isAdding ? 'Adding...' : 'Add Member'}
                    </button>
                </div>
            </form>
        </Dialog>
    );
};

export default AddTeamMemberModal;
